import React, { useMemo } from 'react';
import parseISO from 'date-fns/parseISO';

import { DateFormat, formatDate } from '@lyearn/core/utils/date/formatDate';
import getDuration from '@lyearn/core/utils/date/getDuration';
import { useTranslation } from '@lyearn/i18n';
import { Trans } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Dot, Stack, Typography, TypographyVariants } from '@lyearn/ui';
import { Image as ImageComp } from '@/components/Image';
import { getDescString } from '@/modules/1on1s/views/1on1Details/components/Header/utils';
import {
  ProviderOverrides,
  renderProviderLabel,
} from '@/modules/content/modules/external-content/utils';
import { getWorkspaceIcon } from '@/modules/lyearn-connect/utils';
import { AssignmentTypeEnum, EventTypeEnum, EventUnion, ExternalEventType } from '@/types/schema';

import RecurringSessionIcon from '../../../../components/RecurringSessionIcon';
import { getIsRecurringSession } from '../../../../utils';
import { ASSIGNMENT_TYPE_LABEL_MAP, getUsersName } from '../../utils';
import { sessionNameRootStyles } from './styles';

const PROVIDER_OVERRIDES: ProviderOverrides = {
  Icon: {
    className: 'w-16 h-16 rounded-4',
    width: 16,
    height: 16,
  },
  Label: {
    variant: 'body-short-02',
    color: 'text-text-secondary',
    className: 'ms-6',
  },
};

export const getEventInstructorLabel = (event: EventUnion, instructorsLabel?: string | null) => {
  switch (event.type) {
    case EventTypeEnum.Live:
    case EventTypeEnum.RecurringLive:
      return (
        <Trans
          i18nKey={instructorsLabel ? 'Events:Manager.withInstructor' : 'Common:ResourceType.Live'}
          values={{
            instructor: instructorsLabel,
          }}
        />
      );
    case EventTypeEnum.Offline:
    case EventTypeEnum.RecurringOffline:
      return (
        <Trans
          i18nKey={
            instructorsLabel
              ? 'Events:Manager.OfflineSessionWithInstructor'
              : 'Common:ResourceType.Offline'
          }
          values={{
            instructor: instructorsLabel,
          }}
        />
      );
    case EventTypeEnum.External:
    case EventTypeEnum.RecurringExternal: {
      const { externalEventProviderInfo } = event as ExternalEventType;
      const { provider } = externalEventProviderInfo;

      return (
        <Stack gapX={4}>
          {renderProviderLabel(provider, PROVIDER_OVERRIDES)}
          <Trans
            i18nKey={
              instructorsLabel
                ? 'Events:Manager.ExternalSessionWithInstructor'
                : 'Common:ResourceTypeSM.Session'
            }
            values={{
              instructor: instructorsLabel,
            }}
          />
        </Stack>
      );
    }
  }
};

function ListItemContent({
  Image,
  event,
  showRecurringIcon,
  assignmentType,
  showDuration,
  showInstructors,
  className,
  nameVariant,
}: {
  Image: JSX.Element;
  event: EventUnion;
  showRecurringIcon?: boolean;
  assignmentType?: AssignmentTypeEnum;
  showDuration?: boolean;
  showInstructors?: boolean;
  className?: string;
  nameVariant?: TypographyVariants;
}) {
  const { t } = useTranslation();
  const { name, startTime, instructors, sharedByWorkspace, endTime } = event;

  const instructorsLabel = showInstructors ? getUsersName(t, instructors) : undefined;
  const sessionOn = formatDate(parseISO(startTime), DateFormat.Date);

  const SessionDateLabel = (
    <Typography color="text-text-secondary" variant="body-short-02">
      {t('Events:Manager.SessionOn', { date: sessionOn })}
    </Typography>
  );

  const EventTypeLabel = useMemo(
    () => getEventInstructorLabel(event, instructorsLabel),
    [event, instructorsLabel],
  );

  const isRecurring = event.recurrenceSchedule && getIsRecurringSession(event.recurringCategory);

  const RecurrenceScheduleLabel = event.recurrenceSchedule ? (
    <>
      <Dot className="me-0 ms-0" />
      <Typography color="text-text-secondary" variant="body-short-02">
        {getDescString(t, event.recurrenceSchedule)}
      </Typography>
    </>
  ) : null;

  const AssignmentTypeLabel = assignmentType ? (
    <>
      <Dot className="me-0 ms-0" />
      <Typography className="truncate" color="text-text-secondary" variant="body-short-02">
        {t(ASSIGNMENT_TYPE_LABEL_MAP[assignmentType])}
      </Typography>
    </>
  ) : null;

  const DurationLabel = showDuration ? (
    <>
      <Dot className="me-0 ms-0" />
      <Typography className="truncate" color="text-text-secondary" variant="body-short-02">
        {getDuration(startTime, endTime)}
      </Typography>
    </>
  ) : null;

  return (
    <Stack className={className}>
      {Image}
      <Stack className="truncate ms-16" direction="col">
        <Stack alignItems="center" gapX={8}>
          <Typography
            className={cn(sessionNameRootStyles, 'truncate')}
            color="text-text-primary"
            data-testid="title"
            variant={nameVariant ?? 'label-01'}>
            {name}
          </Typography>
          {showRecurringIcon ? <RecurringSessionIcon /> : null}
        </Stack>
        <Stack className="mt-2">
          {sharedByWorkspace ? (
            <Stack alignItems="center" gapX={6}>
              <Typography color="text-text-secondary" variant="body-short-02">
                {t('Events:Manager.SessionFrom')}
              </Typography>
              <ImageComp
                alt={sharedByWorkspace.workspaceName}
                className="h-16 w-16"
                height={16}
                src={getWorkspaceIcon(sharedByWorkspace)}
                width={16}
              />
              <Typography color="text-text-secondary" variant="label-02">
                {sharedByWorkspace.workspaceName}
              </Typography>
              {isRecurring ? RecurrenceScheduleLabel : null}
              {showDuration ? DurationLabel : !isRecurring ? SessionDateLabel : null}
            </Stack>
          ) : (
            <Stack alignItems="center" className="truncate" gapX={6}>
              {EventTypeLabel ? (
                <Typography
                  className="flex items-center gap-x-4 truncate"
                  color="text-text-secondary"
                  data-testid="instructor"
                  variant="body-short-02">
                  {EventTypeLabel}
                </Typography>
              ) : null}
              {isRecurring ? AssignmentTypeLabel || RecurrenceScheduleLabel : null}
              {showDuration ? DurationLabel : !isRecurring ? SessionDateLabel : null}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}

export default React.memo(ListItemContent);
